import { useQuery } from '@apollo/client'
import { BUNDLE_MARGIN_MATRICES } from '../../graphql/costing-queries'
import LoadingSpinner from '../../components/LoadingSpinner'

/* ─── Helpers ───────────────────────────────────────────────────────────────── */

function formatCents(cents: number): string {
  return `€${(cents / 100).toFixed(2)}`
}

function marginColor(pct: number | null): string {
  if (pct === null) return 'text-gray-400'
  if (pct >= 70) return 'text-green-600'
  if (pct >= 50) return 'text-amber-600'
  return 'text-red-600'
}

/* ─── Types ─────────────────────────────────────────────────────────────────── */

interface BundleGroupOption {
  bundle_group_option_id: number
  eligible_variant_id: number
  eligible_variant_name: string
  eligible_product_name: string
  price_delta_cents: number
  cogs_millicents: number | null
  cogs_cents: number | null
}

interface BundleCombination {
  selected_option_ids: number[]
  total_price_delta_cents: number
  total_cogs_millicents: number | null
  total_cogs_cents: number | null
  upgrade_margin_pct: number | null
}

interface BundleVariant {
  id: number
  name: string
  price_cents: number
}

interface BundleGroup {
  bundle_group_id: number
  name: string
  is_main: boolean
  options: BundleGroupOption[]
}

interface BundleMarginMatrix {
  bundle_product_id: number
  bundle_product_name: string
  bundle_variants: BundleVariant[]
  groups: BundleGroup[]
  combinations: BundleCombination[]
}

function buildLabel(selectedOptionIds: number[], groups: BundleGroup[]): string {
  return selectedOptionIds
    .map((optId) => {
      for (const group of groups) {
        const opt = group.options.find((o) => o.bundle_group_option_id === optId)
        if (opt) return `${opt.eligible_product_name} ${opt.eligible_variant_name}`.trim()
      }
      return '?'
    })
    .join(' + ')
}

/* ─── Rows for one bundle ───────────────────────────────────────────────────── */

const BundleRows = ({ matrix }: { matrix: BundleMarginMatrix }) => {
  const nonMainGroups = matrix.groups.filter((g) => !g.is_main)
  const { combinations, bundle_variants } = matrix

  // Variants to use as price anchors — fall back to a dummy zero-price entry
  // if the bundle has no variants configured yet
  const variants: BundleVariant[] =
    bundle_variants.length > 0
      ? bundle_variants
      : [{ id: 0, name: '', price_cents: 0 }]

  const multiVariant = variants.length > 1

  if (nonMainGroups.length === 0 || combinations.length === 0) {
    return (
      <tr>
        <td className="px-6 py-3 text-sm text-gray-500 whitespace-nowrap">
          {matrix.bundle_product_name}
        </td>
        <td colSpan={5} className="px-6 py-3 text-sm text-gray-400 italic">
          No add-on slots configured
        </td>
      </tr>
    )
  }

  // Each combination × each variant = one display row
  const rows = combinations.flatMap((combo) =>
    variants.map((variant) => ({
      combo,
      variant,
      sellPrice: variant.price_cents + combo.total_price_delta_cents,
      margin:
        combo.total_cogs_cents !== null && variant.price_cents + combo.total_price_delta_cents > 0
          ? ((variant.price_cents + combo.total_price_delta_cents - combo.total_cogs_cents) /
              (variant.price_cents + combo.total_price_delta_cents)) *
            100
          : null,
    }))
  )

  return (
    <>
      {rows.map(({ combo, variant, sellPrice, margin }, idx) => {
        const isFirstRow = idx === 0
        return (
          <tr key={`${combo.selected_option_ids.join('-')}-${variant.id}`} className="hover:bg-gray-50">
            {/* Bundle name — only on the very first row */}
            {isFirstRow ? (
              <td className="px-6 py-3 text-sm text-gray-500 whitespace-nowrap align-top">
                {matrix.bundle_product_name}
              </td>
            ) : (
              <td className="px-6 py-3" />
            )}

            {/* Combination label — repeat per variant when multi-variant */}
            <td className="px-6 py-3 text-sm text-gray-900">
              {buildLabel(combo.selected_option_ids, matrix.groups)}
              {multiVariant && (
                <span className="ml-2 text-xs text-gray-400">({variant.name})</span>
              )}
            </td>

            {/* Sell price */}
            <td className="px-6 py-3 text-sm text-gray-700 whitespace-nowrap">
              {formatCents(sellPrice)}
            </td>

            {/* Add-on COGS */}
            <td className="px-6 py-3 text-sm text-gray-700 whitespace-nowrap">
              {combo.total_cogs_cents === null ? (
                <span className="text-gray-400">—</span>
              ) : (
                formatCents(combo.total_cogs_cents)
              )}
            </td>

            {/* Margin */}
            <td className={`px-6 py-3 text-sm font-medium whitespace-nowrap ${marginColor(margin)}`}>
              {margin !== null ? `${margin.toFixed(1)}%` : '—'}
            </td>
          </tr>
        )
      })}
      <tr className="h-1 bg-gray-100">
        <td colSpan={5} />
      </tr>
    </>
  )
}

/* ─── Page ──────────────────────────────────────────────────────────────────── */

const BundleCostsPage = () => {
  const { data, loading, error } = useQuery(BUNDLE_MARGIN_MATRICES)

  const matrices: BundleMarginMatrix[] = data?.bundleMarginMatrices ?? []

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">Bundle Add-on Costs</h1>
        <p className="mt-1 text-sm text-gray-500">
          Every add-on combination (Side, Drink, Extra) per bundle. Sell price is the bundle base price
          plus any surcharges. Main dish margin is tracked separately on the Variant Costs screen.
        </p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-48">
          <LoadingSpinner size="lg" />
        </div>
      ) : error ? (
        <div className="text-center py-12 text-red-600">{error.message}</div>
      ) : matrices.length === 0 ? (
        <div className="rounded-lg border border-dashed border-gray-300 bg-white p-10 text-center">
          <p className="text-sm text-gray-500">No bundle products found.</p>
        </div>
      ) : (
        <div className="card overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Bundle
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Combination
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Sell price
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Add-on COGS
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Margin
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {matrices.map((matrix) => (
                <BundleRows key={matrix.bundle_product_id} matrix={matrix} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

export default BundleCostsPage
